import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { AutocompleteStyled } from '@/components/atoms/inputs/autocomplete';
import { QUERY_MUNICIPALITIES_WITH_SEARCH } from '@/graphql/queries/municipalities';
import type { MunicipalityWithSearch } from '@/graphql/types/municipality';
import { useTranslation } from '@/hooks/useTranslation';

export interface MunicipalityComparativeModal {
  id?: string;
  name?: string;
}

interface MunicipalityAutocompleteProps {
  index: number;
  municipality: MunicipalityComparativeModal;
  inputValue: string;
  onInputChange: (index: number, value: string) => void;
  onChange: (index: number, value: any) => void;
  municipalitiesComparative: MunicipalityComparativeModal[];
  municipalityData: MunicipalityComparativeModal;
}

export const MunicipalityAutocompleteCompare = ({
  index,
  municipality,
  inputValue,
  onInputChange,
  onChange,
  municipalitiesComparative,
  municipalityData,
}: MunicipalityAutocompleteProps) => {
  const { t } = useTranslation();
  const [debouncedSearch, setDebouncedSearch] = useState(inputValue);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const existData = useMemo(() => {
    if (index > 0 && municipality.id !== '') {
      return true;
    }
    return false;
  }, [index, municipality.id]);

  const shouldApplySearch = debouncedSearch.length >= 3;

  const { data, loading } = useQuery<{
    getAllMunicipalitiesWithSearch: {
      data: MunicipalityWithSearch[];
      total: number;
    };
  }>(QUERY_MUNICIPALITIES_WITH_SEARCH, {
    variables: {
      search: existData ? undefined : shouldApplySearch ? debouncedSearch : '',
      page: 1,
      limit: 20,
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const municipalities = data?.getAllMunicipalitiesWithSearch.data ?? [];

  const options = municipalities.map((municipalityItem: MunicipalityWithSearch) => {
    const isAlreadySelected = municipalitiesComparative.some(
      (selected, i) =>
        i !== index &&
        selected.id &&
        selected.id.trim() !== '' &&
        selected.id === municipalityItem.municipality_code
    );

    return {
      label: municipalityItem.display_name,
      value: municipalityItem.municipality_code,
      disabled: isAlreadySelected,
    };
  });

  return (
    <AutocompleteStyled
      options={options}
      value={municipality.name ? { label: municipality.name, value: municipality.id || '' } : null}
      inputValue={inputValue}
      onInputChange={(_, value) => {
        onInputChange(index, value);
      }}
      onChange={(_, value) => {
        onChange(index, value);
      }}
      margin="none"
      loading={!municipality.id && loading}
      name={`municipality-${municipality.name}`}
      type="text"
      placeholder={t('placeholder.placeholder.search.municipality')}
      disabled={index === 0 && !!municipalityData.id}
      fullWidth
    />
  );
};
